import { useEffect, useRef } from "react";
import {
  redirect,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { useAuthStore } from "../hooks";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { checkGroupFromRole } from "@/helpers";
import { getFirstAllowedRoute } from "@/app/routes/public";
import { AuthStandaloneShell } from "../components";

const Verify2FACodePage = observer(() => {
  const navigate = useNavigate();
  const { t } = useTran();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const email = location.state?.email || searchParams.get("email");
  const token = searchParams.get("token");
  const inputsRef = useRef([]);

  const {
    loading,
    setEmail2FAOtpAt,
    sendEmail2FAResult,
    email2FAOtp,
    sendEmail2FACode,
    verifyEmail2FAOtp,
  } = useAuthStore();

  const sentRef = useRef(false);

  useEffect(() => {
    if (!email || sentRef.current || token) return;

    sentRef.current = true;
    sendEmail2FACode(email);
  }, [email, token, sendEmail2FACode]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const data = await verifyEmail2FAOtp(email);

    if (!data) return;

    const { user } = data;
    const validGroup = checkGroupFromRole(user?.role);
    const redirectRoute = getFirstAllowedRoute(user?.permissions);

    navigate(
      validGroup === "admin_group"
        ? PATHS.USER_LIST
        : validGroup === "bank_group"
          ? PATHS.CARBON_DASHBOARD
          : redirectRoute,
    );
  };

  const handleOtpChange = (index, value) => {
    setEmail2FAOtpAt(index, value);
    const nextRef = inputsRef.current[index + 1];
    if (nextRef && value) nextRef.focus();
  };

  const handleOtpKeyDown = (index, event) => {
    if (event.key === "Backspace" && !email2FAOtp[index] && index > 0) {
      const prevRef = inputsRef.current[index - 1];
      if (prevRef) prevRef.focus();
    }
  };

  return (
    <AuthStandaloneShell>
      <form onSubmit={handleSubmit} className="flex flex-col items-center">
        <h1 className="mb-2 text-center text-2xl font-semibold text-primary sm:text-3xl">
          {t("Verify 2FA Code")}
        </h1>
        <p className="mb-2 text-center text-sm text-muted-foreground">
          {t("Enter the 6-digit verification code sent to")}
        </p>
        <p className="mb-6 break-all text-center text-sm text-muted-foreground">
          <span className="font-medium text-foreground">{email}</span>
        </p>

        <div className="mb-4 grid w-full grid-cols-6 gap-2 sm:gap-3">
          {email2FAOtp.map((digit, index) => (
            <Input
              key={index}
              ref={(element) => (inputsRef.current[index] = element)}
              inputMode="numeric"
              autoComplete={index === 0 ? "one-time-code" : undefined}
              maxLength={1}
              value={digit}
              onChange={(event) => handleOtpChange(index, event.target.value)}
              onKeyDown={(event) => handleOtpKeyDown(index, event)}
              className="h-12 w-full text-center text-lg"
            />
          ))}
        </div>

        {sendEmail2FAResult?.serverError && (
          <p className="mb-4 text-center text-sm text-red-600">
            {sendEmail2FAResult.serverError}
          </p>
        )}

        <Button
          type="submit"
          disabled={loading || email2FAOtp.some((digit) => !digit)}
          className="w-full rounded-full bg-primary hover:bg-primary/90"
        >
          {loading ? t("Verifying...") : t("Verify Code")}
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            email2FAOtp.forEach((_, index) => setEmail2FAOtpAt(index, ""));
            navigate(PATHS.LOGIN);
          }}
          className="mt-4 w-full"
        >
          {t("Back to Login")}
        </Button>
      </form>
    </AuthStandaloneShell>
  );
});

export default Verify2FACodePage;
